import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import Navbar from './components/Navbar';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import AuthCallback from './pages/AuthCallback';
import CompanySetup from './pages/CompanySetup';
import Contact from './pages/Contact';
import AddOns from './pages/AddOns';
import Pricing from './pages/Pricing';
import Services from './pages/Services';
import TrustMonitoring from './pages/TrustMonitoring';
import WhyIAM from './pages/WhyIAM';
import WhyIdnty from './pages/WhyIdnty';
import IdentityDashboard from './pages/IdentityDashboard';
import MonitoringDashboard from './pages/MonitoringDashboard';
import IdentityOnboarding from './pages/onboarding/IdentityOnboarding';
import MonitoringOnboarding from './pages/onboarding/MonitoringOnboarding';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';

// Conditional redirect: if on blog.idnty.one and at root, show Blog instead
function RootRoute() {
  const location = useLocation();
  const isBlogSubdomain = window.location.hostname === 'blog.idnty.one';
  
  if (isBlogSubdomain && location.pathname === '/') {
    return <Blog />;
  }
  return <Home />;
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }
  
  return user ? <Navigate to="/dashboard" /> : children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/add-ons" element={<AddOns />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/services" element={<Services />} />
      <Route path="/trust-monitoring" element={<TrustMonitoring />} />
      <Route path="/why-iam" element={<WhyIAM />} />
      <Route path="/why-idnty" element={<WhyIdnty />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:id" element={<BlogPost />} />
      <Route path="/why-siamang" element={<Navigate to="/why-idnty" replace />} />
      <Route path="/why-kagi" element={<Navigate to="/why-idnty" replace />} />
      <Route path="/monitoring" element={<Navigate to="/trust-monitoring" replace />} />
      <Route path="/identity" element={<Navigate to="/" replace />} />
      <Route path="/external-threat-monitoring" element={<Navigate to="/" replace />} />
      
      {/* Auth Callback */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/dashboard/company-setup" element={<ProtectedRoute><CompanySetup /></ProtectedRoute>} />
      <Route path="/dashboard/monitoring" element={<ProtectedRoute><MonitoringDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/monitoring/onboarding" element={<ProtectedRoute><MonitoringOnboarding /></ProtectedRoute>} />
      <Route path="/dashboard/identity" element={<ProtectedRoute><IdentityDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/identity/onboarding" element={<ProtectedRoute><IdentityOnboarding /></ProtectedRoute>} />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <Navbar />
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
